import { match } from 'ts-pattern'
import { Pin, isValidPin } from './construct/pin'
import { crccs } from './crccs'
import { checkingShouldLock } from './lock-unlock'

export type { Pin }

export interface AgeVerificationOptions {
    readonly shouldSetTime: boolean
    readonly shouldLock: boolean | null
    readonly pin?: Pin
    readonly enableAgeVerification: boolean | null
    readonly underageThreshold?: number
    readonly overageThreshold?: number
    readonly enableExpirationVerification: boolean | null
    readonly pdf417OutputMode?: 'enableAll' | 'enableNoDL' | 'disable'
}

/**
 * Generate data for a qr code that will enable age verification on a scanner
 * @param options configuration options
 * @returns a string suitable for encoding into a QR code
 */
export const ageVerification = ({
    shouldSetTime,
    shouldLock,
    pin,
    enableAgeVerification,
    underageThreshold,
    overageThreshold,
    enableExpirationVerification,
    pdf417OutputMode,
}: AgeVerificationOptions) => {
    if (pin != null && !isValidPin(pin)) throw new Error('Pin is not valid')

    const clockCommand = 'CKDTPDT'
    const underAgeCommand = 'CKAVSUA'
    const overAgeCommand = 'CKAVSOA'
    const avEnableCommand = 'CKAVSEN'
    const exEnableCommand = 'CKAVSEX'
    const ageVerPDF417Command = 'CKAVSAP'

    const commandAcc = []

    if (shouldSetTime) {
        const date = new Date()
        const year = date.getUTCFullYear()
        const month = (date.getUTCMonth() + 1).toString().padStart(2, '0')
        const day = date.getUTCDate().toString().padStart(2, '0')
        const hours = date.getUTCHours().toString().padStart(2, '0')
        const minutes = date.getUTCMinutes().toString().padStart(2, '0')
        const seconds = date.getUTCSeconds().toString().padStart(2, '0')

        commandAcc.push(
            clockCommand +
                `"${year}-${month}-${day} ${hours}:${minutes}:${seconds}"`
        )
    }

    if (underageThreshold != null)
        commandAcc.push(underAgeCommand + underageThreshold)

    if (overageThreshold != null)
        commandAcc.push(overAgeCommand + overageThreshold)

    if (enableAgeVerification === true) commandAcc.push(avEnableCommand + '1')

    if (enableAgeVerification === false) commandAcc.push(avEnableCommand + '0')

    if (enableExpirationVerification === true)
        commandAcc.push(exEnableCommand + '1')

    if (enableExpirationVerification === false)
        commandAcc.push(exEnableCommand + '0')

    if (pdf417OutputMode) {
        commandAcc.push(
            ageVerPDF417Command +
                match(pdf417OutputMode)
                    .with('disable', () => 0)
                    .with('enableNoDL', () => 1)
                    .with('enableAll', () => 2)
                    .exhaustive()
        )
    }

    const lockCommand = checkingShouldLock(shouldLock, pin)
    if (lockCommand) commandAcc.push(lockCommand)

    return crccs(commandAcc)
}
